import { productAddressLine } from '@/utils/productAddress';
import type { LoanApplication } from '@/types/LoanApplicationType';
import type { Product, ProductEsitmation } from '@/types/ProductType';
import type { PropertyView } from '@/types/PropertyView';

export function formatProductAddress(product: Product | undefined | null): {
  line: string;
  county: string;
} {
  if (!product) return { line: '', county: '' };
  const a = product.address ?? {};
  const refi = product.refinance?.property;
  const street = a.street ?? refi?.street;
  const city = a.city ?? refi?.city;
  const state = a.state ?? refi?.state;
  const zip = a.zip ?? refi?.zip;
  const county = (a.county ?? refi?.county ?? '').trim();
  const line =
    (a.fullAddress ?? refi?.fullAddress ?? '').trim() ||
    [street, city, state, zip].filter(Boolean).join(', ').trim() ||
    productAddressLine(product);
  return { line, county };
}

export function resolveSubjectAddress(
  propertyContext: PropertyView | null | undefined,
  product: Product | undefined | null
): { line: string; county: string } {
  if (propertyContext?.address?.trim()) {
    return {
      line: propertyContext.address.trim(),
      county: propertyContext.county?.trim() ?? '',
    };
  }
  return formatProductAddress(product);
}

export function isInvestorPropertyOperatingContext(
  application: Pick<LoanApplication, 'productType' | 'flowProfile'>
): boolean {
  return (
    application.productType === 'dscr' ||
    application.flowProfile === 'investor_dscr'
  );
}

export function operatingFiguresFromSources(
  product: Product | undefined | null,
  estimation: ProductEsitmation | null | undefined
): {
  proposedMonthlyTaxes: number | null;
  proposedMonthlyInsurance: number | null;
  proposedMonthlyRent: number | null;
  proposedMonthlyHoaDues: number | null;
} {
  const li = product?.loanInfo;
  const est = estimation as
    | Record<string, number | undefined>
    | null
    | undefined;

  // A refinance knows the real escrow figures from the mortgage statement —
  // prefer them to the estimate.
  const escrow = product?.refinance?.currentLoan;
  const taxes =
    li?.monthlyPropertyTaxes ??
    escrow?.escrowTaxes ??
    est?.monthlyPropertyTaxes ??
    null;
  const insurance =
    li?.monthlyHomeownersInsurance ??
    escrow?.escrowInsurance ??
    est?.monthlyHomeownersInsurance ??
    null;
  const rent = li?.rent ?? null;
  const hoa = li?.hoaFees ?? null;

  return {
    proposedMonthlyTaxes:
      taxes != null && !Number.isNaN(Number(taxes)) ? Number(taxes) : null,
    proposedMonthlyInsurance:
      insurance != null && !Number.isNaN(Number(insurance))
        ? Number(insurance)
        : null,
    proposedMonthlyRent:
      rent != null && !Number.isNaN(Number(rent)) ? Number(rent) : null,
    proposedMonthlyHoaDues:
      hoa != null && !Number.isNaN(Number(hoa)) ? Number(hoa) : null,
  };
}

export function formatCurrencyField(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '';
  return (Math.round(value * 100) / 100).toLocaleString('en-US', {
    maximumFractionDigits: 2,
  });
}
